
import { Vue, Component, Prop } from 'nuxt-property-decorator';
import type { PropType } from 'vue';
import { isActive } from '@tiptap/vue-2';
import { ColPosAppearance, Responsive } from '@/types/pageContract';
import TooltipButton from '@/components/ui/TooltipButton.vue';
import { File, ImageMetaData } from '@/types/contentContract';
import MetaDataForImageSecond from '~/components/typo3-library-overrides/media/MetaDataForImageSecond.vue';
import MetaDataForImageFirst from '~/components/typo3-library-overrides/media/MetaDataForImageFirst.vue';
import CustomImage from '~/components/typo3-library-overrides/media/type/CustomImage.vue';
import ImageOverlayCarousel from '~/components/typo3-library-overrides/media/ImageOverlayCarousel.vue';
import findParentComponentByName from '~/util/findParentComponentByName';
import ClientSettingsStore from '~/store/clientSettings.store';
import MetaDataForContentFirst from '~/components/typo3-library-overrides/media/MetaDataForContentFirst.vue';

@Component({
  methods: { isActive },
  components: {
    MetaDataForContentFirst,
    MetaDataForImageFirst,
    MetaDataForImageSecond,
    ImageOverlayCarousel,
    CustomImage,
    TooltipButton
  }
})
export default class ImageCarousel extends Vue {
  @Prop({ type: Object as PropType<ColPosAppearance>, default: () => {} })
  appearance!: ColPosAppearance;

  @Prop({ type: Array as PropType<File[]> })
  images!: File[];

  @Prop({ type: Object as PropType<Responsive>, required: true, default: {} })
  responsive!: Responsive;

  @Prop({ type: Object as PropType<ImageMetaData>, default: () => {} })
  imageMetaData!: ImageMetaData;

  overlayActive: boolean = false;

  activeSlide: number = 0;
  preventZoom: boolean = false;
  metaHeight: string = 'auto';
  isPlusSize: boolean = false;
  isFullSize: boolean = false;
  isPlusOrientation: boolean = false;

  get currentDarkMode(): boolean {
    return ClientSettingsStore.darkMode;
  }

  get mediaLabels(): Array<string> {
    return this.imageMetaData.medialabels
      ? this.imageMetaData.medialabels.split('\r\n')
      : [''];
  }

  get textScale() {
    return ClientSettingsStore.currentTextSizeScale;
  }

  isSize(): void {
    if (this.appearance) {
      if (this.appearance.layout.includes('widescreen')) {
        this.isPlusSize = true;
      }
      if (this.appearance.layout.includes('fullscreen')) {
        this.isFullSize = true;
      }
    }
  }

  widthCalc(image) {
    // full
    if (
      this.isFullSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.width < 1251
    ) {
      return (
        this.responsive.wide[image.properties.fileReferenceUid].properties
          .dimensions.width * 0.809
      );
    } else if (
      this.isFullSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.width >= 1251
    ) {
      return 1251;
    }
    // plus
    else if (
      this.isPlusSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.width < 788
    ) {
      return this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.width;
    } else if (
      this.isPlusSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.width < this.isPlusSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.height
    ) {
      return (
        this.responsive.wide[image.properties.fileReferenceUid].properties
          .dimensions.width * 0.809
      );
    } else if (this.isPlusSize && image.properties.dimensions.width >= 788) {
      return 788;
    } else if (
      this.isPlusSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.width *
        0.8 >
        this.responsive.wide[image.properties.fileReferenceUid].properties
          .dimensions.height
    ) {
      this.isPlusOrientation = true;
      return (
        this.responsive.wide[image.properties.fileReferenceUid].properties
          .dimensions.width * 0.6
      );
    } else if (
      this.isPlusSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.width > this.isPlusSize &&
      this.responsive.wide[image.properties.fileReferenceUid].properties
        .dimensions.height
    ) {
      return (
        this.responsive.wide[image.properties.fileReferenceUid].properties
          .dimensions.width * 0.769
      );
    }

    // if Frame
    else if (
      this.isExtendedCmsGroup() > 0 &&
      this.responsive.desktop[image.properties.fileReferenceUid].properties
        .dimensions.width > 408
    ) {
      const paddingAmount = 98 * this.isExtendedCmsGroup(); // Padding amount on extendedcmsgroup;
      return (
        this.responsive.desktop[image.properties.fileReferenceUid].properties
          .dimensions.width *
          0.758 -
        paddingAmount
      );
    }

    // normal
    else if (
      !this.isFullSize &&
      !this.isPlusSize &&
      this.responsive.desktop[image.properties.fileReferenceUid].properties
        .dimensions.width < 538
    ) {
      return (
        this.responsive.desktop[image.properties.fileReferenceUid].properties
          .dimensions.width * 0.967
      );
    } else if (
      !this.isFullSize &&
      !this.isPlusSize &&
      this.responsive.desktop[image.properties.fileReferenceUid].properties
        .dimensions.width > 538
    ) {
      return (
        this.responsive.desktop[image.properties.fileReferenceUid].properties
          .dimensions.width * 0.827
      );
    }
  }

  minHeight() {
    const imageHeightsDesktop = [];
    const imageHeightsWide = [];

    this.images.forEach((image) => {
      imageHeightsDesktop.push(
        Number(
          this.responsive.desktop[image.properties.fileReferenceUid].properties
            .dimensions.height
        )
      );
    });

    this.images.forEach((image) => {
      imageHeightsWide.push(
        Number(
          this.responsive.wide[image.properties.fileReferenceUid].properties
            .dimensions.height
        )
      );
    });

    this.isSize();
    // Plus size
    if (this.isPlusSize) {
      return Math.max(...imageHeightsWide) * 0.769;
    }
    // Full size
    if (this.isFullSize) {
      return Math.max(...imageHeightsWide) * 0.809;
    }

    // frame size
    if (
      this.isExtendedCmsGroup() > 1 &&
      Math.max(...imageHeightsDesktop) > 296
    ) {
      return 296;
    }

    // frame size
    if (
      this.isExtendedCmsGroup() === 1 &&
      Math.max(...imageHeightsDesktop) > 408
    ) {
      return 408;
    }

    // normal size
    if (Math.max(...imageHeightsDesktop) > 538) {
      return 538;
    }
    if (Math.max(...imageHeightsDesktop) < 538) {
      return Math.max(...imageHeightsDesktop) * 0.824;
    }
  }

  minHeightMeta() {
    let maxMetaHeight = 0;
    let maxMetaHeightIndex = -1; // Initialize to an invalid index

    // Iterate through each image
    this.images.forEach((image, index) => {
      // Calculate the total height required for metadata content
      const totalContentHeight =
        image.properties.description.length +
        image.properties.caption.length +
        image.properties.copyright.length +
        image.properties.credits.length +
        this.imageMetaData.imagecaption.length;

      // Compare the calculated height with the current maximum
      if (totalContentHeight > maxMetaHeight) {
        maxMetaHeight = totalContentHeight;
        maxMetaHeightIndex = index;
      }
    });

    // Return the maximum metadata height index
    return maxMetaHeightIndex;
  }

  isExtendedCmsGroup(): number {
    let count = 0;
    let currentComponent = findParentComponentByName(
      'CeExtendedCmsGroup',
      this
    );
    if (findParentComponentByName('CeExtendedCmsGroup', this) !== null) {
      count = 1;
    }
    while (currentComponent) {
      if (
        findParentComponentByName('CeExtendedCmsGroup', currentComponent) !==
        null
      ) {
        count++;
        currentComponent = findParentComponentByName(
          'CeExtendedCmsGroup',
          currentComponent
        );
      } else {
        break; // Exit the loop if no more parents are found
      }
    }

    return count;
  }

  // Example usage:

  mounted() {
    this.$nextTick(() => {
      this.isExtendedCmsGroup();
      this.isSize();
      this.minHeight();
      this.minHeightMeta();
      this.metaHeight =
        this.$refs.metabox[this.minHeightMeta()].clientHeight + 'px';
    });
  }

  closeOverlay(): void {
    this.overlayActive = false;
    this.preventZoom = false;
  }

  showOverlay(): void {
    this.overlayActive = true;
  }
}
